import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useCharacterHudStore, deepMerge } from '../../lib/character-hud-store';
import type { HudCharacterData, DeepPartial } from '../../lib/character-hud-store';
import { createFlushAccumulator } from './flush-accumulator';
import { HUD_TRANSITIONS, type HudState, type FullSheetTab } from './types';
import { useDrag } from './useDrag';
import { useFontLoader } from './useFontLoader';
import { PillState } from './PillState';
import { CombatState } from './CombatState';
import { FullSheetOverlay } from './FullSheetOverlay';
import { DemoControls } from './DemoControls';
import { Dnd5eSheetAdapter } from '@roll1/shared/systems/dnd5e/adapters/hud-adapter.js';
import { srdEquipmentMap, srdSpellMap } from '@roll1/shared/systems/dnd5e/srd/index.js';
import type { SheetReadRole } from '@roll1/shared/systems/dnd5e/adapters/hud-adapter.js';
import { dnd5eRules } from '@roll1/shared/systems/dnd5e/rules/index.js';
import { toCommands } from '@roll1/shared/systems/dnd5e/adapters/write-adapter.js';
import type { RulesCharacterData } from '@roll1/shared/systems/dnd5e/rules/index.js';
import { isSheetOwner } from '@roll1/shared/sheet-permissions.js';
import { useSessionContext } from '../../contexts/SessionContext';
import { useActorContext } from '../../contexts/ActorContext';
import { useSheetContext } from '../../contexts/SheetContext';
import { useInitiativeContext } from '../../contexts/InitiativeContext';
import { useTokenContext } from '../../contexts/TokenContext';
import { useUserUiPrefs } from '../../hooks/useUserUiPrefs';
import { useLayoutContext } from '../../contexts/LayoutContext';
import { toast } from 'sonner';
import { useI18n } from '../../i18n';

// BF-08 / B-03: HUD UI state (hudState, lastTab) persists via the server-backed
// useUserUiPrefs hook (cross-device sync). Bootstrap helpers below pull defaults
// from the prefs object с safe fallbacks if the key shape doesn't match.

function pickHudState(prefs: Record<string, unknown>): HudState | undefined {
  const v = prefs.hudState;
  if (v === 'compact' || v === 'battle') return v;
  // Legacy-персист старой 4-состоянийной машины (pill→mini→combat→docked):
  // статусные состояния → compact, боевые → battle (UX-аудит 2026-07-07).
  if (v === 'pill' || v === 'mini') return 'compact';
  if (v === 'combat' || v === 'docked') return 'battle';
  return undefined;
}

function pickLastTab(prefs: Record<string, unknown>): FullSheetTab | undefined {
  const v = prefs.lastTab;
  if (v === 'combat' || v === 'character' || v === 'roleplay' || v === 'builder') {
    return v;
  }
  return undefined;
}

function pickHudPos(prefs: Record<string, unknown>): { x: number; y: number } | undefined {
  const v = prefs.hudPos as { x?: unknown; y?: unknown } | undefined;
  if (v && typeof v === 'object' && typeof v.x === 'number' && typeof v.y === 'number'
      && Number.isFinite(v.x) && Number.isFinite(v.y)) {
    return { x: v.x, y: v.y };
  }
  return undefined;
}

function pickCollapsedSections(prefs: Record<string, unknown>): string[] {
  const v = prefs.hudBattleCollapsed;
  return Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : [];
}

/**
 * Reconcile an optimistic patch against the authoritative server state: return a
 * copy of `patch` with every leaf the server now CONFIRMS (value-equal in
 * `authoritative`) removed, keeping leaves still in flight. Returns null when
 * nothing remains pending. Objects recurse; arrays/primitives compare by value.
 *
 * Replaces the old "nuke the whole pendingPatch on any echo" clear — which wiped
 * a fresh edit before its OWN echo merged (the HP 13→14→13 flicker), because the
 * server runs recalcComputedFields and broadcasts a WIDE change set, so an echo
 * for any unrelated recomputed field bumped the sheet identity and dropped the
 * in-flight HP overlay.
 */
function pruneConfirmed<T>(
  patch: DeepPartial<T> | null,
  authoritative: T | null | undefined,
): DeepPartial<T> | null {
  if (!patch) return null;
  if (authoritative == null) return patch;
  const out: Record<string, unknown> = {};
  let remaining = 0;
  for (const key of Object.keys(patch as Record<string, unknown>)) {
    const pv = (patch as Record<string, unknown>)[key];
    const av = (authoritative as Record<string, unknown>)[key];
    if (pv !== null && typeof pv === 'object' && !Array.isArray(pv)) {
      const child = pruneConfirmed(pv as DeepPartial<unknown>, av as unknown);
      if (child !== null) { out[key] = child; remaining++; }
    } else if (Array.isArray(pv)) {
      const isPrimitiveArray = pv.every((x) => x === null || typeof x !== 'object');
      if (isPrimitiveArray && Array.isArray(av)) {
        // Set-semantic primitive arrays (resistances/immunities/vulnerabilities,
        // proficiencies) round-trip byte-identically, so keep the optimistic
        // value until the authoritative array set-matches — otherwise an
        // unrelated echo drops it early and the chips flicker back to the old
        // value before this write's own echo arrives.
        const a = [...(pv as unknown[])].map(String).sort();
        const b = [...(av as unknown[])].map(String).sort();
        const equal = a.length === b.length && a.every((x, i) => x === b[i]);
        if (!equal) { out[key] = pv; remaining++; }
      }
      // else: server-authoritative object arrays (attacks, spells.known,
      // inventory, activeEffects, …) are REBUILT by the adapter in a different
      // shape and never byte-match — drop on the first echo (rawData changed).
    } else {
      // Scalar leaf: keep only while genuinely in-flight (≠ authoritative).
      if (pv !== av) { out[key] = pv; remaining++; }
    }
  }
  return remaining > 0 ? (out as DeepPartial<T>) : null;
}

interface CharacterHudOverlayProps {
  visible?: boolean;
  /** Direct sheet opening without token selection (fallback for CharacterPanel, JournalEntryEditor) */
  directSheetId?: string | null;
  /** Callback to clear directSheetId (called on selection change or overlay close) */
  onClearDirectSheetId?: () => void;
  /**
   * Optional WebSocket dispatcher override. The production mount
   * (CanvasArea) does NOT pass it — the overlay resolves `mp.send` from
   * SessionContext (see effectiveSend below), which is the live write path
   * for FullSheetOverlay → CombatTab and the embedded states. Kept as a prop
   * so tests and alternative mounts can inject a dispatcher.
   */
  send?: (msg: unknown) => void;
}

function canTransition(from: HudState, to: HudState): boolean {
  return HUD_TRANSITIONS[from].includes(to);
}

/** Determine SheetReadRole based on session context.
 *
 * Ownership MUST go through the shared isSheetOwner (same source the server's
 * filterSheetForRole uses) — a hand-rolled check here drifts from the server
 * and mis-gates. The old version compared sheet.ownerId (a userId, and never
 * populated at runtime — SessionHydrator doesn't hydrate it) against the
 * participant UUID (mp.participant.id), so the 'owner' branch could never fire
 * and every owner fell through to 'party-basic' → minimalMapping → a blanked
 * sheet. isSheetOwner falls back to ownerName === participantName, which is the
 * only signal that actually survives (there is no owner_id column). */
function resolveRole(
  isGM: boolean,
  sheet: Parameters<typeof isSheetOwner>[0],
  participantName: string | null | undefined,
  userId: string | null | undefined,
  viewOtherSheets: string,
): SheetReadRole {
  if (isGM) return 'gm';
  if (isSheetOwner(sheet, participantName ?? '', userId ?? undefined)) return 'owner';
  return viewOtherSheets === 'full' ? 'party-full' : 'party-basic';
}

export function CharacterHudOverlay({
  visible = true,
  directSheetId,
  onClearDirectSheetId,
  send: sendProp,
}: CharacterHudOverlayProps) {
  const { t } = useI18n();

  // Demo mode: EXPLICIT ?hud-demo query flag only (Playwright visual specs,
  // DemoControls). Until 2026-07-03 `import.meta.env.DEV` also forced demo,
  // which made the real token→sheet→HUD chain untestable in `vite dev` —
  // now dev runs the real adapter/WS path like production.
  const isDemoMode = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('hud-demo');

  // Session context for role resolution and sheet data
  const { mp, isGM, sessionSettings } = useSessionContext();
  const { actorSheetId } = useActorContext();
  const { sheets: charSheets, templates } = useSheetContext();

  // T002: Effective send — explicit prop from AppShell wins over the
  // SessionContext-bound mp.send. Demo mode logs mutations instead of dropping them
  // so primitives stay interactive for visual validation.
  const effectiveSend = useMemo<((msg: unknown) => void) | undefined>(() => {
    if (isDemoMode) {
      return (msg: unknown) => console.log('[demo-send]', msg);
    }
    return sendProp ?? (mp.send as (msg: unknown) => void);
  }, [isDemoMode, sendProp, mp.send]);

  // Resolve effective sheetId: explicit open (directSheetId) wins while the
  // full sheet is open; otherwise the HUD is pinned to the participant's ACTOR
  // (ActorContext). Canvas selection no longer drives the HUD — clicking
  // tokens is map interaction, not «кем я играю» (см. ActorContext).
  const effectiveSheetId = isDemoMode ? null : (directSheetId ?? actorSheetId);

  // BF-08 / B-03: HUD UI state persists через server-backed user.uiPreferences
  // (cross-device sync). Local React state remains the render source of truth;
  // useUserUiPrefs supplies initial values и a setter that ships changes к the
  // server. Server's authoritative echo arrives via a separate effect (below)
  // and hydrates local state if it diverges от the initial mount value.
  const uiPrefs = useUserUiPrefs();
  const [hudState, setHudState] = useState<HudState>(
    (): HudState => pickHudState(uiPrefs.prefs) ?? 'compact',
  );
  const [fullSheetOpen, setFullSheetOpen] = useState<boolean>(false);
  const [hudVisible, setHudVisible] = useState<boolean>(true);
  // BF-16: Remember last tab
  const [lastTab, setLastTab] = useState<FullSheetTab>(
    (): FullSheetTab => pickLastTab(uiPrefs.prefs) ?? 'combat',
  );
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Auto-open FullSheetOverlay when directSheetId is set
  useEffect(() => {
    if (directSheetId && !fullSheetOpen) {
      setFullSheetOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directSheetId]);

  // Resolve the effective sheet object once. Its identity changes ONLY when
  // THIS sheet is updated (useCharacterSheets.handleUpdated preserves the
  // identity of untouched sheets), so it doubles as the optimistic-overlay
  // reset signal below — broadcasts about OTHER sheets must not wipe a
  // pending patch mid-flight.
  const effectiveSheet = useMemo(() => {
    if (isDemoMode || !effectiveSheetId) return null;
    return charSheets.sheets.find((s: { id: string }): boolean => s.id === effectiveSheetId) ?? null;
  }, [isDemoMode, effectiveSheetId, charSheets.sheets]);

  // Resolve sheet and adapter data
  const adaptedData = useMemo<HudCharacterData | null>(() => {
    const sheet = effectiveSheet;
    if (!sheet) return null;

    const template = templates.templates.find((tpl: { id: string }): boolean => tpl.id === sheet.templateId);
    if (!template) return null;

    const role = resolveRole(
      isGM,
      sheet,
      mp.participant?.name,
      mp.participant?.userId,
      sessionSettings.playerPermissions?.viewOtherSheets ?? 'basic',
    );

    const result = Dnd5eSheetAdapter.fromSheet(sheet, template, role, srdSpellMap, srdEquipmentMap);
    if (result.warnings.length > 0) {
      console.warn('[CharacterHudOverlay] adapter warnings:', result.warnings);
    }

    // T002: Adapter returns the structural supertype RulesCharacterData. Hud is
    // the narrower view of the same runtime shape — single `as` narrowing is
    // sufficient (no `as unknown as` double-cast). The adapter's implementation
    // produces values that satisfy HudCharacterData's stricter field shapes
    // (required identity.name/race/className, narrower union literals, etc.).
    return result.data as HudCharacterData;
  }, [effectiveSheet, templates.templates, isGM, mp.participant?.id, sessionSettings.playerPermissions?.viewOtherSheets]);

  // dispatch is defined below; declare via ref to avoid forward-reference issue
  const dispatchRef = useRef<((diff: DeepPartial<HudCharacterData>) => void) | null>(null);

  // Determine the store mode: adapter data or singleton
  const storeOpts = useMemo(() => {
    if (isDemoMode) return undefined; // demo: use singleton store with DEFAULT_CHARACTER
    if (adaptedData) return {
      sheetData: adaptedData,
      rules: dnd5eRules,
      // BF-001/BF-005 fix: route store.patch through dispatch for optimistic + WS
      onPatch: (diff: DeepPartial<HudCharacterData>): void => dispatchRef.current?.(diff),
    };
    return { rules: dnd5eRules }; // no token selected, use singleton
  }, [isDemoMode, adaptedData]);

  const { data: rawData, store, helpers, rules } = useCharacterHudStore(storeOpts);

  // Optimistic overlay — pendingPatch over adapted data
  const [pendingPatch, setPendingPatch] = useState<DeepPartial<HudCharacterData> | null>(null);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reconcile pendingPatch against the authoritative server state when an echo
  // lands (FR-015: server = source of truth). PRUNE only the leaves the server
  // now confirms, keeping still-in-flight edits — instead of nuking the whole
  // overlay on any identity bump. The old nuke wiped a fresh HP edit before its
  // own echo merged (recalcComputedFields broadcasts a wide change set → the
  // sheet identity changed on an unrelated field), producing the 13→14→13
  // flicker. Pruning is safe even when this fires on an unrelated broadcast: a
  // leaf is dropped only when its value actually equals the authoritative one.
  useEffect(() => {
    setPendingPatch((prev) => {
      const next = pruneConfirmed(prev, rawData);
      // Reference-stabilise: if the prune result is content-equal to the current
      // pending patch, keep the SAME reference. pruneConfirmed always builds a
      // fresh object, so without this a content-equal result would set a new
      // reference every time `rawData` re-renders, and any consumer that maps a
      // patch back through store.patch would ping-pong → "Maximum update depth
      // exceeded" (React #185). Now React bails out via Object.is.
      if (prev === next) return prev;
      if (prev != null && next != null && JSON.stringify(prev) === JSON.stringify(next)) return prev;
      return next;
    });
  }, [rawData]);

  // Once the overlay is fully reconciled (or cleared), drop the 5s write-timeout
  // so it can't fire a spurious "write timed out" toast after a successful echo.
  useEffect(() => {
    if (pendingPatch === null && pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
  }, [pendingPatch]);

  // Latest-value refs so the 5s write-timeout (armed inside a debounced closure)
  // can re-check the CURRENT authoritative state at fire time instead of the
  // stale snapshot captured when it was armed — a slow-but-successful write
  // whose echo lands just after 5s should NOT show a "write timed out" toast.
  const rawDataRef = useRef(rawData);
  useEffect(() => { rawDataRef.current = rawData; }, [rawData]);
  const pendingPatchRef = useRef(pendingPatch);
  useEffect(() => { pendingPatchRef.current = pendingPatch; }, [pendingPatch]);

  const data = useMemo(() => {
    if (pendingPatch && rawData) return deepMerge(rawData, pendingPatch);
    return rawData;
  }, [rawData, pendingPatch]);

  // Dispatch debounce ref for rapid-fire operations. The accumulator owns the
  // debounced diff + the write target captured at ACCUMULATION time — so a
  // debounced send (or the flush-on-switch effect) ships to the sheet the edit
  // was made on, never to whichever sheet is selected when the timer fires
  // (the cross-sheet-write race, unit-tested in flush-accumulator.test.ts).
  const dispatchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const accumulatorRef = useRef(
    createFlushAccumulator<HudCharacterData, RulesCharacterData>(
      // HudCharacterData widens structurally to RulesCharacterData (T002).
      toCommands as (diff: DeepPartial<RulesCharacterData>, rawData: RulesCharacterData, sheetId: string) => unknown[],
      deepMerge,
    ),
  );

  const flushAccumulated = useCallback((armTimeout: boolean): void => {
    const cmds = accumulatorRef.current.flush();
    if (cmds.length === 0) return;

    // Timeout: clear pending patch if server doesn't respond. Arm it only for
    // NON-rp commands. rp echoes DO reconcile rawData now (they mirror into
    // the per-sheet store via useCharacterSheets.handleRp*), but their
    // payloads are granular ops the server may re-shape (list normalisation,
    // scope filtering), so byte-level prune confidence is lower — keep the
    // write-timeout unarmed for rp to avoid false "write timed out" toasts.
    const nonRpCmds = cmds.filter((c): boolean => !String((c as { type?: string }).type ?? '').startsWith('sheet:rp-'));
    if (armTimeout && nonRpCmds.length > 0) {
      if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = setTimeout((): void => {
        // Re-prune against the LATEST authoritative state: if the server has
        // since confirmed every leaf (echo arrived just after the deadline),
        // stay silent; only revert + toast for genuinely unconfirmed leaves.
        const stillUnconfirmed = pruneConfirmed(pendingPatchRef.current, rawDataRef.current);
        setPendingPatch(null);
        if (stillUnconfirmed !== null) toast(t('characterHud.writeTimeout'));
      }, 5000);
    }
  }, [t]);

  const dispatch = useCallback((diff: DeepPartial<HudCharacterData>): void => {
    if (!effectiveSheetId || isDemoMode) return;

    // Accumulate optimistic patch
    setPendingPatch((prev: DeepPartial<HudCharacterData> | null): DeepPartial<HudCharacterData> =>
      prev ? deepMerge(prev, diff) : diff,
    );

    // Debounce WS send (300ms). Capture the target NOW: effectiveSheetId +
    // current rawData + the resolved dispatcher (prop override > mp.send; the
    // production CanvasArea mount passes no prop, so this is mp.send).
    accumulatorRef.current.accumulate(diff, {
      sheetId: effectiveSheetId,
      rawData,
      send: (effectiveSend ?? mp.send) as (msg: unknown) => void,
    });

    if (dispatchTimerRef.current) clearTimeout(dispatchTimerRef.current);
    dispatchTimerRef.current = setTimeout((): void => {
      dispatchTimerRef.current = null;
      flushAccumulated(true);
    }, 300);
  }, [effectiveSheetId, isDemoMode, rawData, effectiveSend, mp, flushAccumulated]);

  // Keep dispatchRef in sync so onPatch (created in useMemo before dispatch) can reach the latest closure
  useEffect(() => {
    dispatchRef.current = dispatch;
  }, [dispatch]);

  // Flush-on-switch: pendingPatch/accumulatedDiff belong to the sheet they
  // were edited on. When the effective sheet changes (token switch, deselect,
  // unbind), ship any debounced diff to its ORIGINAL target immediately and
  // drop the optimistic overlay — otherwise the stale overlay leaks visually
  // onto the new sheet and the 5s write-timeout false-fires against the new
  // sheet's authoritative data.
  const prevDispatchSheetIdRef = useRef<string | null>(effectiveSheetId);
  useEffect(() => {
    if (prevDispatchSheetIdRef.current === effectiveSheetId) return;
    prevDispatchSheetIdRef.current = effectiveSheetId;
    if (dispatchTimerRef.current) {
      clearTimeout(dispatchTimerRef.current);
      dispatchTimerRef.current = null;
    }
    flushAccumulated(false);
    setPendingPatch(null);
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
  }, [effectiveSheetId, flushAccumulated]);

  // Cleanup on unmount — ship (not drop) an in-flight debounced edit.
  useEffect(() => {
    return () => {
      if (dispatchTimerRef.current) clearTimeout(dispatchTimerRef.current);
      if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
      flushAccumulated(false);
    };
  }, [flushAccumulated]);

  // ROOT CAUSE 3: Lifecycle event — handle sheet deletion while HUD is open
  const prevSheetIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isDemoMode) return;

    // If we were showing a sheet that no longer exists, notify user
    if (prevSheetIdRef.current && effectiveSheetId === prevSheetIdRef.current) {
      const sheetStillExists = charSheets.sheets.some((s: { id: string }): boolean => s.id === prevSheetIdRef.current);
      if (!sheetStillExists) {
        toast.info(t('characterHud.sheetDeleted'));
        // Clear directSheetId if it was the deleted sheet. (Compare BEFORE
        // nulling the ref — the old order compared against the already-nulled
        // ref, so the clear never fired for a real deletion.)
        if (directSheetId === prevSheetIdRef.current) {
          onClearDirectSheetId?.();
        }
        prevSheetIdRef.current = null;
        return;
      }
    }

    prevSheetIdRef.current = effectiveSheetId;
  }, [isDemoMode, effectiveSheetId, charSheets.sheets, directSheetId, onClearDirectSheetId, t]);

  useFontLoader();

  // Hydrate local state when the server reply arrives с different values
  // (initial mount used optimistic localStorage fallback). Strict compare
  // avoids ping-pong с the persist effects below.
  useEffect(() => {
    const next = pickHudState(uiPrefs.prefs);
    if (next !== undefined && next !== hudState) setHudState(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiPrefs.prefs.hudState]);

  useEffect(() => {
    const next = pickLastTab(uiPrefs.prefs);
    if (next !== undefined && next !== lastTab) setLastTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiPrefs.prefs.lastTab]);

  // BF-08: Persist HUD state changes (server-backed via uiPrefs.setPref).
  useEffect(() => {
    if (pickHudState(uiPrefs.prefs) === hudState) return; // no diff, skip dispatch
    uiPrefs.setPref('hudState', hudState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hudState]);

  // BF-16: Persist last tab changes.
  useEffect(() => {
    if (pickLastTab(uiPrefs.prefs) === lastTab) return;
    uiPrefs.setPref('lastTab', lastTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastTab]);

  // Default spot: TOP, just left of the sidebar — the old bottom-right default
  // sat under the sidebar and lost its position on every reload (owner feedback
  // 2026-07-08). The battle panel is 400px wide; anchor for that width so the
  // compact pill (216px) lands in the same corridor.
  const { sidebarWidth } = useLayoutContext().state;
  // Set when the user drags BEFORE the server prefs arrive: the restore
  // effect must not yank the HUD back to the stale saved spot, and the
  // fresh drag must persist once `ready` flips (writing earlier would land
  // on the initial {} and be overwritten by the authoritative echo).
  const userMovedEarlyRef = useRef<{ x: number; y: number } | null>(null);
  const uiPrefsRef = useRef(uiPrefs);
  uiPrefsRef.current = uiPrefs;
  const { position, handlePointerDown, isDragging, setPosition } = useDrag({
    initialPosition: {
      x: typeof window !== 'undefined' ? Math.max(8, window.innerWidth - (sidebarWidth ?? 360) - 400 - 16) : 800,
      y: 16,
    },
    elementRef: containerRef,
    // Persist the final spot cross-device (same channel as hudState/lastTab).
    onDragEnd: (pos) => {
      if (uiPrefsRef.current.ready) {
        uiPrefsRef.current.setPref('hudPos', pos);
      } else {
        userMovedEarlyRef.current = pos;
      }
    },
  });

  // Restore the persisted position once the server prefs arrive. Gated on
  // `ready` (same contract as ThemeSync): applying against the initial `{}`
  // would keep the default and then fight the authoritative echo.
  const posRestoredRef = useRef(false);
  useEffect(() => {
    if (!uiPrefs.ready || posRestoredRef.current) return;
    posRestoredRef.current = true;
    const early = userMovedEarlyRef.current;
    if (early) {
      // The user already placed the HUD by hand this session — their spot
      // wins over the persisted one; write it through now that we're ready.
      userMovedEarlyRef.current = null;
      uiPrefs.setPref('hudPos', early);
      return;
    }
    const saved = pickHudPos(uiPrefs.prefs);
    if (saved) setPosition(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiPrefs.ready]);

  // Battle-panel collapsed sections + pinned skills — persisted in uiPrefs.
  // Pins are per-character (a rogue pins Stealth, a cleric pins Insight).
  // Both writers are gated on uiPrefs.ready: a toggle computed from the
  // initial {} would overwrite the cross-device value before it ever arrived
  // (same contract as ThemeSync / hudPos below).
  const collapsedSections = pickCollapsedSections(uiPrefs.prefs);
  const toggleSection = useCallback((id: string) => {
    if (!uiPrefs.ready) return;
    const cur = pickCollapsedSections(uiPrefs.prefs);
    uiPrefs.setPref('hudBattleCollapsed', cur.includes(id) ? cur.filter((s) => s !== id) : [...cur, id]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiPrefs.prefs, uiPrefs.ready]);

  const pinnedKey = `hudPinnedSkills:${effectiveSheetId ?? 'none'}`;
  const pinnedSkills = useMemo(() => {
    const v = uiPrefs.prefs[pinnedKey];
    return Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : [];
  }, [uiPrefs.prefs, pinnedKey]);
  const togglePinSkill = useCallback((skill: string) => {
    if (!uiPrefs.ready) return;
    const cur = pinnedSkills;
    uiPrefs.setPref(pinnedKey, cur.includes(skill) ? cur.filter((s) => s !== skill) : [...cur, skill]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedSkills, pinnedKey, uiPrefs.ready]);

  // Авто-battle: наступил ход АКТОРА в инициативе → compact сам раскрывается
  // в боевую панель (симметрично автоследованию актора за инициативой у GM).
  // Обратно не сворачивается — только руками (collapse).
  const { initiative } = useInitiativeContext();
  const { tokens: tokensHook } = useTokenContext();
  useEffect(() => {
    if (isDemoMode || !effectiveSheetId) return;
    const entry = initiative.entries[initiative.currentIndex];
    if (!entry) return;
    const token = tokensHook.tokens.find((tk: { id: string }) => tk.id === entry.tokenId);
    if ((token as { characterSheetId?: string | null } | undefined)?.characterSheetId === effectiveSheetId) {
      setHudState('battle');
    }
    // Намеренно только по смене хода — не по каждому изменению токенов/листа.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiative.currentIndex]);

  // Live-раунд и «чей ход» для баннера боевой панели: адаптерный
  // data.combat.round читает values.round, которое никто не пишет (всегда 0).
  const isMyTurn = useMemo(() => {
    if (isDemoMode || !effectiveSheetId) return false;
    const entry = initiative.entries[initiative.currentIndex];
    if (!entry) return false;
    const token = tokensHook.tokens.find((tk: { id: string }) => tk.id === entry.tokenId);
    return (token as { characterSheetId?: string | null } | undefined)?.characterSheetId === effectiveSheetId;
  }, [isDemoMode, effectiveSheetId, initiative.entries, initiative.currentIndex, tokensHook.tokens]);

  const transition = useCallback((to: HudState): void => {
    if (canTransition(hudState, to)) {
      setHudState(to);
    }
  }, [hudState]);

  const forceState = useCallback((state: HudState): void => {
    setHudState(state);
  }, []);

  const openFullSheet = useCallback((): void => {
    setFullSheetOpen(true);
  }, []);

  const closeFullSheet = useCallback((currentTab?: FullSheetTab): void => {
    if (currentTab) setLastTab(currentTab);
    setFullSheetOpen(false);
    // Clear directSheetId on overlay close
    onClearDirectSheetId?.();
  }, [onClearDirectSheetId]);

  const toggleVisibility = useCallback((): void => {
    setHudVisible((prev: boolean): boolean => !prev);
  }, []);

  const resetStore = useCallback((): void => {
    store.reset();
  }, [store]);

  // Хоткей H — скрыть/показать HUD (раньше toggleVisibility был доступен
  // только из DemoControls). Не срабатывает в полях ввода и с модификаторами.
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key !== 'h' && e.key !== 'H' && e.key !== 'р' && e.key !== 'Р') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      toggleVisibility();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggleVisibility]);

  if (!visible) return null;

  // T002 (Feature #6): Loading skeleton for the transient state where a sheet is
  // bound (effectiveSheetId truthy) but adaptedData has not yet resolved. Demo
  // mode and the 'no sheet bound' case (effectiveSheetId === null) fall through
  // to existing behavior below.
  if (effectiveSheetId && adaptedData === null && !isDemoMode) {
    return (
      <div data-theme="paper">
        <div
          className="fixed rounded-lg bg-white/10 animate-pulse"
          style={{
            left: position.x,
            top: position.y,
            width: 216,
            height: 96,
            zIndex: 'var(--z-sidebar)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          }}
          role="status"
          aria-label={t('common.loading') ?? 'Loading character'}
          aria-busy="true"
        />
      </div>
    );
  }

  // Empty state: in production, no bound sheet means NO HUD. Without this
  // gate the store falls back to the singleton (DEFAULT_CHARACTER / last
  // localStorage snapshot) and the HUD shows a phantom character on
  // deselect, on tokens without a sheet, on multi-select, and after another
  // client deletes the token. Demo mode intentionally keeps the singleton.
  if (!isDemoMode && !effectiveSheetId) return null;

  // Don't render if store has no data
  if (!data?.identity?.name) return null;

  const dragHandleProps = { onPointerDown: handlePointerDown };

  return (
    <div data-theme="paper">
      {isDemoMode && (
        <DemoControls
          hudState={hudState}
          onSetHudState={forceState}
          onToggleVisibility={toggleVisibility}
          onOpenFullSheet={openFullSheet}
          onResetStore={resetStore}
        />
      )}

      {/* Floating HUD hides while the full sheet is open — the pill peeking
          out from under the overlay read as a random artifact. */}
      {hudVisible && !fullSheetOpen && (
        <div
          ref={containerRef}
          className="fixed rounded-lg"
          style={{
            left: position.x,
            top: position.y,
            zIndex: 'var(--z-sidebar)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            transition: isDragging ? 'none' : 'width 300ms ease, height 300ms ease',
            cursor: isDragging ? 'grabbing' : undefined,
          }}
        >
          {hudState === 'compact' && (
            <PillState
              data={data}
              helpers={helpers}
              onExpand={(): void => transition('battle')}
              onOpenFullSheet={openFullSheet}
              dragHandleProps={dragHandleProps}
            />
          )}
          {hudState === 'battle' && (
            <CombatState
              data={data}
              store={store}
              helpers={helpers}
              onCollapse={(): void => transition('compact')}
              onOpenFullSheet={openFullSheet}
              dragHandleProps={dragHandleProps}
              send={effectiveSend}
              sheetId={effectiveSheetId ?? undefined}
              onMessage={isDemoMode ? undefined : mp.onMessage}
              collapsedSections={collapsedSections}
              onToggleSection={toggleSection}
              pinnedSkills={pinnedSkills}
              onTogglePinSkill={togglePinSkill}
              currentRound={initiative.round}
              isMyTurn={isMyTurn}
            />
          )}
        </div>
      )}

      {/* BF-16: Pass lastTab as initialTab to restore previous tab on reopen */}
      {/* V008: Pass adapted data and rules to FullSheetOverlay */}
      {/* Forward effectiveSend (prop override > mp.send; production mount
          passes no prop) so the chain CharacterHudOverlay → FullSheetOverlay
          → CombatTab carries a defined dispatcher and CombatTab.isReadOnly
          resolves to false. */}
      {/* HP-reactivity fix: pass the OVERLAID `data` (rawData + pendingPatch),
          not the raw adaptedData. The full sheet is where edits happen; with
          raw data the optimistic overlay never reached CombatTab, so a stepper
          click moved nothing until debounce(300ms) + server echo, and rapid
          clicks all computed the same absolute target (N clicks = 1 step). */}
      <FullSheetOverlay
        open={fullSheetOpen}
        onClose={closeFullSheet}
        initialTab={lastTab}
        adaptedData={adaptedData ? data : null}
        rules={rules}
        send={effectiveSend}
        sheetId={effectiveSheetId ?? undefined}
        onMessage={isDemoMode ? undefined : mp.onMessage}
        // Bug fix 2026-06-01: forward dispatch к FullSheetOverlay's internal store.
        // Without this CombatTab adapter store stays read-only и silently drops все
        // +/- mutations (regression от 24efc59b 2026-05-12 «wire adapter to frontend»).
        onPatch={(diff: DeepPartial<HudCharacterData>): void => dispatchRef.current?.(diff)}
      />
    </div>
  );
}
